import type { HTMLAttributes, ReactNode, UIEvent } from 'react';
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useModifiers } from '../../styles/hooks/useModifiers';

import styles from './overscrollable.module.css';

export interface OverscrollableProps extends HTMLAttributes<HTMLDivElement> {
  footer?: string | ReactNode;
  horizontal?: boolean;
  center?: boolean;
  scrollToTop?: boolean;
}

const Overscrollable = forwardRef<HTMLDivElement, OverscrollableProps>(function OverscrollableFunc(
  { children, className = '', footer, horizontal, center, scrollToTop, ...rest },
  forwardedRef
) {
  const fallbackRef = useRef<HTMLDivElement>(null);
  const ref = forwardedRef || fallbackRef;
  const [isTopOverflowing, setIsTopOverflowing] = useState(false);
  const [isBottomOverflowing, setIsBottomOverflowing] = useState(false);

  const mods = useModifiers(styles, 'overscrollable', {
    horizontal: horizontal,
    center: center,
  });

  const resizeObserver = useRef(
    new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target as HTMLDivElement;
        handleScroll(target);
      });
    })
  );

  const handleScroll = useCallback((target: HTMLDivElement): void => {
    const overflowTop = target.scrollTop;
    const overflowBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    setIsTopOverflowing(overflowTop > 1);
    setIsBottomOverflowing(overflowBottom > 1);
  }, []);

  useLayoutEffect(() => {
    if (ref && 'current' in ref && ref.current) {
      resizeObserver.current.observe(ref.current);
      if (scrollToTop) ref.current.scrollTo({ top: 0, behavior: 'instant' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToTop]);

  return (
    <div className={`${styles.overscrollable} ${mods}`}>
      <div
        className={`${styles.overscrollable__body} ${
          isTopOverflowing ? styles.overscrollable__body_overflowTop : ''
        }  ${isBottomOverflowing ? styles.overscrollable__body_overflowBottom : ''} ${className}`}
        ref={ref}
        {...rest}
        onScroll={(event: UIEvent<HTMLDivElement>): void => handleScroll(event.target as HTMLDivElement)}
      >
        {children}
      </div>
      {footer ? <div className={styles.overscrollable__footer}>{footer}</div> : null}
    </div>
  );
});

Overscrollable.displayName = 'Overscrollable';

export { Overscrollable };
